import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { getContent } from '@plone/volto/actions/content/content';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { usePagination } from '@plone/volto/helpers/Utils/usePagination';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';

import config from '@plone/volto/registry';
import { withQuerystringKey } from '../../../../../helpers/querystringKey';
import { BROWSE_METADATA_FIELDS } from '../../../../../helpers/browseFields';

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default function withQuerystringResults(WrappedComponent) {
  function WithQuerystringResults(props) {
    const {
      data = {},
      id = data.block,
      properties: content,
      path,
      variation,
    } = props;
    const { settings } = config;
    const querystring = data.querystring || data; // For backwards compat with data saved before Blocks schema. Note, this is also how the Search block passes data to ListingBody
    const subrequestID = content?.UID ? `${content?.UID}-${id}` : id;
    const { b_size = settings.defaultPageSize } = querystring; // batchsize

    // save the path so it won't trigger dispatch on eager router location change
    const [initialPath] = React.useState(getBaseUrl(path));

    // SHADOW: core forwards only these five keys to @querystring-search, which
    // silently dropped the search app's CONTROL params. Widened with:
    //   mode          explicit search|browse intent (item 1) -- without it the
    //                 seam falls back to guessing from whether a term exists,
    //                 and an empty search box lands on the stock catalog
    //   status_group  Current|Archive|All (item 6)
    //   operator      AND|OR (item 5)
    //   correct       the literal text to leave UNCORRECTED (item 21). The
    //                 search app POSTs its own request, so this is the only
    //                 route by which the URL's opt-out reaches the seam.
    // They are top-level params, NOT querystring rows, deliberately: a row with
    // an unknown index would be handed to plone.app.querystring on the catalog
    // fallback path, which does not know these names.
    const copyFields = [
      'limit',
      'query',
      'sort_on',
      'sort_order',
      'depth',
      'mode',
      'status_group',
      'operator',
      'correct',
    ];
    const { currentPage, setCurrentPage } = usePagination(id, 1);
    const adaptedQuery = Object.assign(
      variation?.fullobjects
        ? { fullobjects: 1 }
        : // `family` (documents|delegations|glossary|people) is stamped on
          // the 12 canonical browse blocks' own stored data, but `data` HERE
          // is `listingBodyData` -- the querystring-shaped object
          // SearchBlockView builds via `withControls(applyDefaults(...))`,
          // which never carries `family` (`applyDefaults` rebuilds its
          // return value from `searchData` -- query rows, sort, b_size --
          // plus `withControls`'s four named CONTROL_FIELDS; neither lists
          // `family`). `variation` is the one that does: SearchBlockView
          // passes `variation={{ ...data, ...listingBodyVariation }}`,
          // spreading the WHOLE block data (same object `resolveBlockData`
          // reads `family` off of on the server) in before the chosen
          // template config -- exactly the object `variation?.fullobjects`
          // above already reads a block-level flag from. Every other search
          // block -- the Search App and any plain listing (news, etc, reached
          // through fullobjects above or not at all here) -- keeps `_all`.
          // Must equal the SERVER's choice in Browse/getSearchBlockAsyncData.js
          // exactly (same constant, same array): helpers/querystringKey.js
          // folds `metadata_fields` into the request key it compares against
          // the SSR seed's key below, and array order is not normalised, only
          // object keys are.
          {
            metadata_fields: variation?.family
              ? BROWSE_METADATA_FIELDS
              : '_all',
          },
      {
        b_size: b_size,
      },
      ...copyFields.map((name) =>
        Object.keys(querystring).includes(name)
          ? { [name]: querystring[name] }
          : {},
      ),
    );
    const adaptedQueryRef = useRef(adaptedQuery);
    const currentPageRef = useRef(currentPage);

    const querystringResults = useSelector(
      (state) => state.querystringsearch.subrequests,
    );
    const dispatch = useDispatch();

    const folderItems = content?.is_folderish ? content.items : [];
    const hasQuery = querystring?.query?.length > 0;
    const hasLoaded = hasQuery
      ? querystringResults?.[subrequestID]?.loaded
      : true;

    const listingItems = hasQuery
      ? querystringResults?.[subrequestID]?.items || []
      : folderItems;

    const showAsFolderListing = !hasQuery && content?.items_total > b_size;
    const showAsQueryListing =
      hasQuery && querystringResults?.[subrequestID]?.total > b_size;

    const totalPages = showAsFolderListing
      ? Math.ceil(content.items_total / b_size)
      : showAsQueryListing
        ? Math.ceil(querystringResults[subrequestID].total / b_size)
        : 0;

    const prevBatch = showAsFolderListing
      ? content.batching?.prev
      : showAsQueryListing
        ? querystringResults[subrequestID].batching?.prev
        : null;
    const nextBatch = showAsFolderListing
      ? content.batching?.next
      : showAsQueryListing
        ? querystringResults[subrequestID].batching?.next
        : null;

    const isImageGallery =
      (!data.variation && data.template === 'imageGallery') ||
      data.variation === 'imageGallery';

    useDeepCompareEffect(() => {
      if (hasQuery) {
        const action = withQuerystringKey(
          getQueryStringResults(
            initialPath,
            adaptedQuery,
            subrequestID,
            currentPage,
          ),
        );
        // SHADOW: core re-sends the request on every mount, including the
        // hydration of a page whose server render already answered it (Browse/
        // getSearchBlockAsyncData stamps the same key). Skip it when the
        // subrequest holds a successful result for exactly this request. This
        // removes a duplicate Solr POST per browse page view, and it removes
        // the browse pages' dependence on a rendering crawler being able and
        // willing to call /++api++/ at all (see helpers/querystringKey).
        const seeded = querystringResults?.[subrequestID];
        if (!(seeded?.loaded && seeded.queryKey === action.queryKey)) {
          dispatch(action);
        }
      } else if (isImageGallery && !hasQuery) {
        // when used as image gallery, it doesn't need a query to list children
        dispatch(
          getQueryStringResults(
            initialPath,
            {
              ...adaptedQuery,
              b_size: 10000000000,
              query: [
                {
                  i: 'path',
                  o: 'plone.app.querystring.operation.string.relativePath',
                  v: '',
                },
              ],
            },
            subrequestID,
          ),
        );
      } else {
        dispatch(getContent(initialPath, null, null, currentPage));
      }
      adaptedQueryRef.current = adaptedQuery;
      currentPageRef.current = currentPage;
    }, [
      subrequestID,
      isImageGallery,
      adaptedQuery,
      hasQuery,
      initialPath,
      dispatch,
      currentPage,
    ]);

    return (
      <WrappedComponent
        {...props}
        onPaginationChange={(e, { activePage }) => setCurrentPage(activePage)}
        total={querystringResults?.[subrequestID]?.total}
        batch_size={b_size}
        currentPage={currentPage}
        totalPages={totalPages}
        prevBatch={prevBatch}
        nextBatch={nextBatch}
        listingItems={listingItems}
        hasLoaded={hasLoaded}
        isFolderContentsListing={showAsFolderListing}
        // What the seam actually did (mode, applied status group, effective
        // sort, operator, scope). Kept alive by the addon's querystringsearch
        // reducer; null on the stock-catalog path, which every consumer treats
        // as "no extra information". The results table needs it to show the
        // effective sort (item 19) when the caller deliberately sent none.
        searchMeta={querystringResults?.[subrequestID]?.search_meta || null}
      />
    );
  }

  WithQuerystringResults.displayName = `WithQuerystringResults(${getDisplayName(
    WrappedComponent,
  )})`;

  return hoistNonReactStatics(WithQuerystringResults, WrappedComponent);
}
